import { LitElement, html, css, unsafeHTML } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'

/**
 * Skio Universal Plan Picker
 * @docs https://integrate.skio.com/
 *
 * Uses LitJS (<5kb) https://lit.dev/ for reactive web components
 */

export class SkioPlanPicker extends LitElement {
  static properties = {
    key: { type: String },

    product: { type: Object },
    productHandle: { type: String },
    selectedVariant: { type: Object },

    // Supply bundle products (60/90 day) serialized by skio-app-block.liquid
    // from the base product's custom.supply_product_60/90 metafields.
    supplyProducts: { type: Array },
    selectedSupplyDays: { state: true },
    freqOpen: { state: true },

    options: { type: Object },

    moneyFormat: { type: String },

    quantity: { type: Number },
    rules: { type: Array },
    rule: { type: Object },
    cart: { type: Object },

    availableSellingPlanGroups: { state: true },
    selectedSellingPlanGroup: { type: Object },
    selectedSellingPlan: { type: Object },

    disableUrl: { type: Boolean },

    debug: { type: Boolean },
  }

  static styles = [
    css`
      :host {
        width: 100%;
      }

      .skio-plan-picker {
        display: block;
        padding: 0;
        border: 0;
        font-size: 15px;
        color: #1a1a1a;
        width: 100%;
        margin: 0 0 1rem;
        font-family: inherit;
      }

      :host([layout='hidden']) .skio-plan-picker {
        display: none;
      }

      .sr-only {
        position: absolute;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        border: 0;
      }

      /* ---- Plan tabs: Subscribe & Save | One-Time Purchase ---- */
      .plan-tabs {
        display: flex;
        align-items: baseline;
        gap: 22px;
        padding: 0 2px 12px;
        border-bottom: 1px solid #e6e6e6;
        margin-bottom: 14px;
      }

      .plan-tab {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        font-family: inherit;
        font-size: 15px;
        font-weight: 700;
        letter-spacing: -0.005em;
        color: #a0a0a0;
        position: relative;
        transition: color 0.15s;
      }

      .plan-tab:hover,
      .plan-tab.on {
        color: #1a1a1a;
      }

      .plan-tab:focus-visible {
        outline: 2px #ccc solid;
        outline-offset: 4px;
      }

      .plan-tab.on::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: -13px;
        height: 2px;
        background: #1a1a1a;
      }

      /* Only the active panel takes up space, so the box hugs its content
         (no reserved gap under the shorter one-time panel). */
      .tab-panel {
        display: none;
      }

      .tab-panel.on {
        display: block;
      }

      /* ---- Stacked options (radio left, label middle, price right) ---- */
      .options-stack {
        display: flex;
        flex-direction: column;
        gap: 14px;
      }

      .opt-stack {
        position: relative;
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 10px;
        padding: 12px;
        min-height: 74px;
        box-sizing: border-box;
        background: #fff;
        border: 2px solid #e4e4e4;
        border-radius: 8px;
        cursor: pointer;
        transition: border-color 0.18s, box-shadow 0.18s;
      }

      .opt-stack:hover {
        border-color: #8a8a8a;
      }

      .opt-stack.on {
        border-color: #1a1a1a;
        box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
      }

      .opt-stack:has(input:focus-visible) {
        outline: 2px #ccc solid;
        outline-offset: 3px;
      }

      .opt-stack .radio {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid #c8c8c8;
        display: inline-block;
        flex: none;
        position: relative;
        box-sizing: border-box;
        transition: border-color 0.18s;
      }

      .opt-stack.on .radio {
        border-color: #1a1a1a;
      }

      .opt-stack.on .radio::after {
        content: '';
        position: absolute;
        inset: 3px;
        border-radius: 50%;
        background: #1a1a1a;
      }

      .opt-main {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .opt-label {
        font-size: 14.5px;
        font-weight: 800;
        letter-spacing: -0.01em;
        color: #1a1a1a;
        line-height: 1.15;
      }

      .opt-sub {
        font-size: 11.5px;
        color: #5a5a5a;
        line-height: 1.25;
      }

      .opt-price {
        display: grid;
        grid-template-columns: auto auto;
        column-gap: 6px;
        row-gap: 5px;
        align-items: baseline;
        justify-items: end;
        text-align: right;
        flex: none;
      }

      .opt-price .was {
        grid-column: 1;
        grid-row: 1;
        font-size: 12px;
        font-weight: 500;
        color: #9a9a9a;
        line-height: 1;
        margin: 0;
      }

      .opt-price .now {
        grid-column: 2;
        grid-row: 1;
        font-size: 18px;
        font-weight: 800;
        letter-spacing: -0.015em;
        line-height: 1;
        color: #1a1a1a;
      }

      .opt-price.no-was .now {
        grid-column: 1 / span 2;
      }

      .opt-price .tag {
        grid-column: 1 / span 2;
        grid-row: 2;
        justify-self: end;
        margin: 0;
        display: inline-block;
        background: #555;
        color: #fff;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.03em;
        padding: 2px 8px;
        border-radius: 5px;
        white-space: nowrap;
      }

      /* "Best Value" pill above the highest-discount option */
      .best-pill {
        position: absolute;
        top: -11px;
        left: 50%;
        transform: translateX(-50%);
        background: #f9bfd8;
        color: #7a2e52;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 4px 12px;
        border-radius: 6px;
        white-space: nowrap;
        z-index: 2;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
      }

      /* ---- Benefits row (subscription panel) ---- */
      .benefits {
        display: flex;
        justify-content: space-between;
        gap: 6px;
        margin-top: 18px;
      }

      .benefit {
        flex: 1;
        text-align: center;
      }

      .benefit .bi {
        width: 35px;
        height: 35px;
        border-radius: 50%;
        background: #e7e7e6;
        color: #6b6b6b;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 7px;
      }

      .benefit .bi svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
      }

      .benefit p {
        font-size: 11px;
        line-height: 1.25;
        color: #555;
        margin: 0;
      }

      /* ---- Upsell card (one-time panel) ---- */
      .upsell-card {
        margin-top: 18px;
        background: linear-gradient(135deg, #fde9f1 0%, #fff5f9 100%);
        border: 1px solid #f6c8db;
        border-radius: 10px;
        padding: 10px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      }

      .upsell-headline {
        font-size: 11.5px;
        font-weight: 800;
        letter-spacing: -0.01em;
        line-height: 1.2;
        color: #7a2e52;
        flex: 1;
        min-width: 0;
        white-space: nowrap;
      }

      .upsell-switch {
        background: #fff;
        color: #7a2e52;
        border: 1.5px solid #ee7fb0;
        border-radius: 6px;
        padding: 6px 10px;
        cursor: pointer;
        font-family: inherit;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        white-space: nowrap;
        flex: 0 0 auto;
        transition: background 0.15s, color 0.15s, transform 0.12s;
      }

      .upsell-switch:hover {
        background: #7a2e52;
        color: #fff;
      }

      .upsell-switch:active {
        transform: scale(0.98);
      }

      /* ---- Desktop sizing ---- */
      @media only screen and (min-width: 750px) {
        .opt-stack {
          gap: 12px;
          padding: 14px;
          min-height: 84px;
        }
        .opt-price {
          column-gap: 8px;
          row-gap: 6px;
        }
        .opt-price .was {
          font-size: 14px;
        }
        .opt-price .now {
          font-size: 22px;
        }
        .opt-price .tag {
          font-size: 11px;
          padding: 3px 10px;
          border-radius: 6px;
        }
        .benefits {
          gap: 8px;
          margin-top: 20px;
        }
        .benefit .bi {
          width: 36px;
          height: 36px;
        }
        .upsell-card {
          margin-top: 20px;
        }
        .upsell-headline {
          font-size: 12.5px;
          letter-spacing: -0.005em;
        }
        .upsell-switch {
          padding: 7px 12px;
          font-size: 11px;
        }
      }

      .group-content-additional {
        margin-top: 10px;
        font-size: 12px;
        color: #555;
      }

      .group-content-additional ul {
        margin: 0;
        padding-left: 18px;
      }

      .group-content-additional p {
        margin: 0;
      }

      .skio-details {
        --text-color: #333;
        --text-color-secondary: #888;

        user-select: none;
        -webkit-user-select: none;
        margin-bottom: 20px;
        order: 3;
        max-width: max-content;
      }

      .skio-details summary::-webkit-details-marker,
      .skio-details summary::marker,
      .skio-details slot {
        color: rgba(0, 0, 0, 0) !important;
      }

      .skio-details summary {
        margin-top: 15px;
      }

      .skio-details summary span {
        font-size: 0.9em;
        display: flex;
        padding: 0.5em 0;
        cursor: pointer;
        align-items: center;
        gap: 10px;
        text-decoration: underline;

        margin-top: -40px;
        color: #000;
      }

      @keyframes fadeInDown {
        0% {
          opacity: 0;
          transform: translateY(-15px);
        }
        100% {
          opacity: 1;
          transform: translateY(0px);
        }
      }
      .skio-details[open] > .skio-details--content {
        animation-name: fadeInDown;
        animation-duration: 0.3s;
      }

      .skio-details--content {
        position: absolute;
        z-index: 1020;
        padding: 1em;
        width: fit-content;
        border-radius: 5px;
        background: white;
        box-shadow: 0 0 5px rgb(23 24 24 / 5%), 0 1px 2px rgb(0 0 0 / 7%);
      }

      .skio-details ul {
        margin: 0;
        padding: 0;
      }

      .skio-details ul li {
        display: flex;
        align-items: flex-start;
        gap: 0.75em;

        margin-bottom: 1em;
      }

      .skio-details .skio-content {
        display: flex;
        flex-direction: column;
      }

      .skio-details .skio-content p {
        font-size: 0.9em;

        margin-top: 0;
        margin-bottom: 0;

        letter-spacing: 0;
        line-height: 1.5;

        color: var(--text-color);
      }

      .skio-details ul li small {
        font-size: 0.7em;
        color: var(--text-color-secondary);
      }

      .skio-details .skio-icon {
        display: flex;

        width: 2.25em;
        height: 2.25em;

        color: var(--text-color);
        background: #f8f8f8;
        border-radius: 100%;

        flex-shrink: 0;
        align-items: center;
        justify-content: center;
      }

      .skio-details .skio-icon svg {
        width: 1.25em;
        height: 1.25em;

        color: inherit;
      }

      .skio-details--footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.9em;
      }

      .skio-details--footer a {
        color: var(--text-color);
      }

      .skio-manage-link {
        text-decoration: underline;
      }

      .powered-by-skio {
        font-size: 0.8em;

        display: flex;
        text-decoration: none;

        align-items: center;
        gap: 3px;
      }

      .setupMode {
        position: relative;
        color: black;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 1rem;
        font-size: 14px;
        border-style: solid;
        border-width: 4px;
        border-image: repeating-linear-gradient(-55deg, #000, #000 20px, #ffb101 20px, #ffb101 40px) 10;
      }

      .setupMode:after {
        content: 'Setup Mode';
        position: absolute;
        top: 0;
        right: 0;
        background-color: black;
        color: white;
        padding: 0.25rem 1rem;
        font-size: 13px;
      }

      .setupMode .detection {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 10px;
      }

      .setupMode .detection small {
        grid-column: 1/-1;
      }

      span.subscription-badge {
        padding: 2px 6px;
        background-color:  #F9728C !important;
        border-radius: 4px;
        font-size: 12px;
        color: #ffffff !important;
        white-space: nowrap;
        margin-left: 25px;
      }

      /* ---- Subscribe & Save two-card layout ---- */
      .ss-cards {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
        align-items: stretch;
        margin: 12px 0 16px;
      }

      .ss-card {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        gap: 4px;
        text-align: center;
        padding: 20px 12px 18px;
        box-sizing: border-box;
        background: var(--skio-group-background-color, #fff);
        border: var(--skio-group-border-width, 2px) solid var(--skio-group-border-color, #e4e4e4);
        border-radius: var(--skio-group-border-radius, 10px);
        cursor: pointer;
        transition: border-color 0.18s, box-shadow 0.18s;
      }

      .ss-card:hover {
        border-color: #8a8a8a;
      }

      .ss-card.on {
        border-color: var(--skio-group-border-selected-color, #1a1a1a);
        background: var(--skio-group-background-selected-color, #fff);
        box-shadow: 0 1px 0 rgba(0, 0, 0, 0.04);
      }

      .ss-card:has(input:focus-visible) {
        outline: 2px #ccc solid;
        outline-offset: 3px;
      }

      .ss-card-title {
        font-size: 15px;
        font-weight: 800;
        font-style: italic;
        letter-spacing: -0.01em;
        color: var(--skio-group-text-color, #1a1a1a);
        line-height: 1.2;
      }

      .ss-card-price {
        display: flex;
        align-items: baseline;
        justify-content: center;
        gap: 7px;
        flex-wrap: wrap;
      }

      .ss-card-price .now {
        font-size: 24px;
        font-weight: 800;
        letter-spacing: -0.015em;
        color: #1a1a1a;
        line-height: 1.1;
      }

      .ss-card-price .was {
        font-size: 14px;
        font-weight: 500;
        color: #9a9a9a;
      }

      .ss-card-sub {
        font-size: 12px;
        color: #5a5a5a;
        line-height: 1.25;
      }

      .ss-badge {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.03em;
        padding: 3px 12px;
        border-radius: 6px;
        white-space: nowrap;
        z-index: 2;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
      }

      .ss-badge--save {
        top: -11px;
        background: var(--skio-discount-color, #2b2b2b);
        color: var(--skio-discount-text-color, #fff);
      }

      .ss-badge--best {
        bottom: -11px;
        background: #f9bfd8;
        color: #7a2e52;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .ss-freq-line {
        font-size: 12px;
        color: #5a5a5a;
        line-height: 1.25;
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }

      .ss-freq {
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        cursor: pointer;
        font-family: inherit;
        font-size: 12px;
        color: #1a1a1a;
        text-decoration: underline;
        text-underline-offset: 2px;
        display: inline-flex;
        align-items: center;
        gap: 3px;
        white-space: nowrap;
      }

      .ss-freq svg {
        width: 11px;
        height: 11px;
        fill: currentColor;
        flex: none;
      }

      .ss-freq-menu {
        position: absolute;
        left: 50%;
        bottom: 4px;
        transform: translate(-50%, 100%);
        display: flex;
        flex-direction: column;
        min-width: 150px;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
        padding: 4px;
        z-index: 10;
      }

      .ss-freq-opt {
        background: none;
        border: none;
        border-radius: 6px;
        padding: 8px 10px;
        cursor: pointer;
        font-family: inherit;
        font-size: 12.5px;
        color: #1a1a1a;
        text-align: left;
        transition: background 0.12s;
      }

      .ss-freq-opt:hover {
        background: #f4f4f4;
      }

      .ss-freq-opt.on {
        font-weight: 800;
        background: #f0f0f0;
      }

      @media only screen and (min-width: 750px) {
        .ss-card {
          padding: 22px 14px 20px;
        }
        .ss-card-title {
          font-size: 16px;
        }
        .ss-card-price .now {
          font-size: 27px;
        }
        .ss-card-price .was {
          font-size: 15px;
        }
        .ss-card-sub,
        .ss-freq-line,
        .ss-freq {
          font-size: 13px;
        }
      }
    `,
  ]

  constructor() {
    super()

    // Start - Debug Variables
    this.debug = this.debug || window?.Shopify?.designMode
    this.variantChanged = false
    // End - Debug Variables

    this.rules = []
    this.rule = null
    this.quantity = 1
    this.showDetailsHover = false

    // Supply layout state: null = one-time / uninitialized, 30|60|90 = selected option.
    // supplySelection holds the bundle {product, variant, plan, allocation} the form
    // must submit instead of the base variant (null when 30-day/base is selected).
    this.selectedSupplyDays = null
    this.supplySelection = null
    this._lastSupplyDays = 30
    this.freqOpen = false
  }

  async connectedCallback() {
    super.connectedCallback()

    this.log('Mounted')

    if (this.productHandle) {
      this.loading = true
      this.fetchProduct(this.productHandle)
    }

    this.bindFormEvents()
    this.bindCartEvents()

    this.rules = await this.fetchRules()
    await this.getRule()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this._captureAdd) {
      document.removeEventListener('click', this._captureAdd, true)
      document.removeEventListener('submit', this._captureAdd, true)
      this._captureAdd = null
    }
  }

  // SECTION: Element Templates
  piggyIcon() {
    return html`<svg viewBox="0 0 640 640" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M320 32C373 32 416 75 416 128C416 181 373 224 320 224C267 224 224 181 224 128C224 75 267 32 320 32zM80 368C80 297.9 127 236.6 197.1 203.1C222.4 244.4 268 272 320 272C375.7 272 424.1 240.3 448 194C463.8 182.7 483.1 176 504 176L523.5 176C533.9 176 541.5 185.8 539 195.9L521.9 264.2C531.8 276.6 540.1 289.9 546.3 304L568 304C581.3 304 592 314.7 592 328L592 440C592 453.3 581.3 464 568 464L528 464C511.5 486 489.5 503.6 464 514.7L464 544C464 561.7 449.7 576 432 576L399 576C384.7 576 372.2 566.5 368.2 552.8L361.1 528L278.8 528L271.7 552.8C267.8 566.5 255.3 576 241 576L208 576C190.3 576 176 561.7 176 544L176 514.7C119.5 490 80 433.6 80 368zM456 384C469.3 384 480 373.3 480 360C480 346.7 469.3 336 456 336C442.7 336 432 346.7 432 360C432 373.3 442.7 384 456 384z"
      />
    </svg>`
  }

  certificateIcon() {
    return html`<svg viewBox="0 0 640 640" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M271.2 56C265.1 49.8 256.2 47.3 247.8 49.6C239.4 51.9 232.9 58.4 230.8 66.8L215.5 127C214.4 131.4 209.9 134 205.6 132.7L145.8 115.9C137.4 113.5 128.4 115.9 122.3 122C116.2 128.1 113.8 137.1 116.2 145.5L133.1 205.3C134.3 209.6 131.7 214.1 127.4 215.2L67.1 230.5C58.7 232.6 52.1 239.2 49.8 247.6C47.5 256 50 264.9 56.2 271L100.7 314.3C103.9 317.4 103.9 322.6 100.7 325.8L56.3 369.1C50.1 375.2 47.6 384.1 49.9 392.5C52.2 400.9 58.8 407.4 67.2 409.6L127.4 424.9C131.8 426 134.4 430.5 133.1 434.8L116.2 494.5C113.8 502.9 116.2 511.9 122.3 518C128.4 524.1 137.4 526.5 145.8 524.1L205.6 507.2C209.9 506 214.4 508.6 215.5 512.9L230.8 573.1C232.9 581.5 239.5 588.1 247.9 590.4C256.3 592.7 265.2 590.2 271.3 584L314.6 539.5C317.7 536.3 322.9 536.3 326.1 539.5L369.3 584C375.4 590.2 384.3 592.7 392.7 590.4C401.1 588.1 407.6 581.5 409.8 573.1L425.1 513C426.2 508.6 430.7 506 435 507.3L494.8 524.2C503.2 526.6 512.2 524.2 518.3 518.1C524.4 512 526.8 503 524.4 494.6L507.5 434.8C506.3 430.5 508.9 426 513.2 424.9L573.4 409.6C581.8 407.5 588.4 400.9 590.7 392.5C593 384.1 590.5 375.1 584.3 369.1L539.8 325.8C536.6 322.7 536.6 317.5 539.8 314.3L584.3 271C590.5 264.9 593 256 590.7 247.6C588.4 239.2 581.8 232.7 573.4 230.5L513.2 215.2C508.8 214.1 506.2 209.6 507.5 205.3L524.4 145.5C526.8 137.1 524.4 128.1 518.3 122C512.2 115.9 503.2 113.5 494.8 115.9L435 132.8C430.7 134 426.2 131.4 425.1 127.1L409.8 66.8C407.7 58.4 401.1 51.8 392.7 49.5C384.3 47.2 375.4 49.7 369.3 55.9L326 100.5C322.9 103.7 317.7 103.7 314.5 100.5L271.2 56z"
      />
    </svg>`
  }

  pauseIcon() {
    return html`<svg viewBox="0 0 640 640" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM288 256L288 384C288 401.7 273.7 416 256 416C238.3 416 224 401.7 224 384L224 256C224 238.3 238.3 224 256 224C273.7 224 288 238.3 288 256zM416 256L416 384C416 401.7 401.7 416 384 416C366.3 416 352 401.7 352 384L352 256C352 238.3 366.3 224 384 224C401.7 224 416 238.3 416 256z"
      />
    </svg>`
  }

  // Display title for a selling plan group (honors the subscription/prepaid title options)
  groupTitle(group) {
    return group.name !== 'Prepaid' && this.options?.subscription_title
      ? this.options?.subscription_title
      : group.name == 'Prepaid' && this.options?.prepaid_title
      ? this.options?.prepaid_title
      : group.name
  }

  // Derive "30 Day Supply" / "Every 30 days" style labels from the selling plan
  // name. Falls back to the raw plan name when no interval can be parsed.
  planLabel(selling_plan) {
    const match = selling_plan.name.match(/(\d+)\s*(day|week|month|year)s?/i)

    if (match) {
      const count = Number(match[1])
      const unit = match[2].toLowerCase()
      const unitTitle = unit.charAt(0).toUpperCase() + unit.slice(1)
      return {
        label: `${count} ${unitTitle} Supply`,
        sub: `Every ${count} ${unit}${count === 1 ? '' : 's'}`,
      }
    }

    return { label: selling_plan.name, sub: '' }
  }

  // Strikethrough price for a plan row: full price before the plan discount,
  // or the variant compare-at price when there is no plan discount.
  planComparePrice(selling_plan) {
    if (!this.options?.show_compare_price) return null

    const discount = this.discount(selling_plan)
    if (discount.absolute > 0) return this.price(selling_plan, false) + discount.absolute

    const variant = this.selectedVariant
    if (variant.compare_at_price && variant.compare_at_price > variant.price) return variant.compare_at_price

    return null
  }

  // The plan with the highest discount in a group gets the "Best Value" pill
  bestValuePlan(group) {
    const plans = this.getAvailableSellingPlans(group)
    if (plans.length < 2) return null

    let best = null
    let bestPercent = 0

    plans.forEach(selling_plan => {
      const percent = this.discount(selling_plan).percent
      if (percent > 0 && percent >= bestPercent) {
        best = selling_plan
        bestPercent = percent
      }
    })

    return best
  }

  maxDiscountPercent() {
    let max = 0

    this.availableSellingPlanGroups?.forEach(group => {
      this.getAvailableSellingPlans(group).forEach(selling_plan => {
        max = Math.max(max, this.discount(selling_plan).percent || 0)
      })
    })

    return max
  }

  // SECTION: Supply layout (30/60/90 day bundle products)

  // 'supply' when the base product has linked 60/90-day bundle products,
  // otherwise the Subscribe & Save 'cards' layout. Block setting overrides for QA.
  get layoutMode() {
    const style = this.options?.picker_style
    if (style === 'supply' || style === 'cards') return style
    return this.supplyProducts?.length ? 'supply' : 'cards'
  }

  // Parse a selling plan name into a day count ("Every 30 Days" -> 30). Infinity when unparsable.
  planIntervalDays(selling_plan) {
    const match = selling_plan?.name?.match(/(\d+)\s*(day|week|month|year)s?/i)
    if (!match) return Infinity
    const count = Number(match[1])
    const factor = { day: 1, week: 7, month: 30, year: 365 }[match[2].toLowerCase()] || 1
    return count * factor
  }

  // The base product's plan used for the "30 Day Supply" row. The base product may
  // still carry legacy 60/90-day plans; prefer an explicit 30-day plan, then the
  // shortest interval, so this keeps working once Skio removes the extra plans.
  supplyBasePlan() {
    const groups = this.availableSellingPlanGroups || []
    const plans = groups.flatMap(group => this.getAvailableSellingPlans(group))
    if (!plans.length) return null
    return plans.find(plan => /\b30\s*day/i.test(plan.name)) || plans.slice().sort((a, b) => this.planIntervalDays(a) - this.planIntervalDays(b))[0]
  }

  // Map the selected base variant onto a bundle product variant:
  // exact option-values match -> title match -> first available.
  mapBundleVariant(bundle) {
    if (!bundle?.variants?.length) return null
    const norm = value => String(value ?? '').trim().toLowerCase()
    const selOptions = (this.selectedVariant?.options || []).map(norm).join('|')
    const selTitle = norm(this.selectedVariant?.title)

    const byOptions = bundle.variants.find(variant => (variant.options || []).map(norm).join('|') === selOptions)
    const byTitle = bundle.variants.find(variant => norm(variant.title) === selTitle)
    const firstAvailable = bundle.variants.find(variant => variant.available)

    return [byOptions, byTitle, firstAvailable].find(variant => variant && variant.available) || null
  }

  // Pick the bundle's selling plan matching its supply interval (a 60-day bundle
  // ships "Every 60 Days"), falling back to any plan the variant is allocated to.
  bundlePlanFor(bundle, variant, days) {
    const plans = (bundle.selling_plan_groups || []).flatMap(group => group.selling_plans || [])
    const allocationFor = plan => (variant.allocations || []).find(allocation => allocation.selling_plan_id === plan.id)
    const intervalRe = new RegExp(`\\b${days}\\s*day`, 'i')

    const plan = plans.find(p => intervalRe.test(p.name) && allocationFor(p)) || plans.find(p => allocationFor(p))
    if (!plan) return null

    return { plan, allocation: allocationFor(plan) }
  }

  // Ordered option descriptors for the supply layout rows.
  supplyOptions() {
    const options = []

    const basePlan = this.supplyBasePlan()
    if (basePlan) {
      options.push({
        days: 30,
        isBase: true,
        product: this.product,
        variant: this.selectedVariant,
        plan: basePlan,
        price: this.price(basePlan, false),
        compare: this.planComparePrice(basePlan),
      })
    }

    ;(this.supplyProducts || []).forEach(bundle => {
      if (!bundle?.available) return
      const variant = this.mapBundleVariant(bundle)
      if (!variant) return
      const match = this.bundlePlanFor(bundle, variant, bundle.days)
      if (!match) return

      const option = {
        days: bundle.days,
        isBase: false,
        product: bundle,
        variant,
        plan: match.plan,
        allocation: match.allocation,
        price: match.allocation.price,
      }
      option.compare = this.supplyComparePrice(option)
      options.push(option)
    })

    return options.sort((a, b) => a.days - b.days)
  }

  // Discount for a supply option, computed against ITS OWN variant's price
  // (getDiscountFromPriceAdjustment always uses the base selectedVariant).
  supplyDiscount(option) {
    const discount = { percent: 0, amount: 0, absolute: 0 }
    if (option.isBase) return this.discount(option.plan)

    const adjustment = option.plan?.price_adjustments?.[0]
    if (!adjustment) return discount

    const price = option.variant.price
    const compareAtPrice = option.variant.compare_at_price && option.variant.compare_at_price > price ? option.variant.compare_at_price : price

    switch (adjustment.value_type) {
      case 'percentage':
        discount.percent = adjustment.value
        discount.absolute = Math.round((price * adjustment.value) / 100.0)
        discount.amount = discount.absolute
        break
      case 'fixed_amount':
        discount.percent = Math.round(((adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = adjustment.value
        discount.amount = adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice - adjustment.value) * 1.0) / compareAtPrice) * 100.0)
        discount.absolute = compareAtPrice - adjustment.value
        discount.amount = price - adjustment.value
        break
    }

    return discount
  }

  supplyDiscountText(option) {
    const discount = this.supplyDiscount(option)
    if (!discount.percent || discount.percent === Infinity || discount.percent < 0) return ''

    return unsafeHTML(
      (this.options?.discount_text || '[discount] OFF')
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[future_price_adjustments]', '')
    )
  }

  // Strikethrough price for a bundle row: allocation compare price, price before
  // the plan discount, or the bundle variant's compare-at price.
  supplyComparePrice(option) {
    if (!this.options?.show_compare_price) return null

    if (option.allocation?.compare_at_price && option.allocation.compare_at_price > option.price) return option.allocation.compare_at_price

    const discount = this.supplyDiscount(option)
    if (discount.absolute > 0) return option.price + discount.absolute

    const variant = option.variant
    if (variant.compare_at_price && variant.compare_at_price > variant.price) return variant.compare_at_price

    return null
  }

  bestSupplyOption(options) {
    if (options.length < 2) return null

    let best = null
    let bestPercent = 0
    options.forEach(option => {
      const percent = this.supplyDiscount(option).percent
      if (percent > 0 && percent >= bestPercent) {
        best = option
        bestPercent = percent
      }
    })

    return best
  }

  // Select a supply option. The base (30-day) row flows through the normal
  // selling-plan path; bundle rows keep the base variant in [name="id"] and
  // stash the bundle variant/plan for the submit-time swap.
  selectSupply(days) {
    const option = this.supplyOptions().find(o => o.days === days)
    if (!option) return

    this.selectedSupplyDays = days
    this._lastSupplyDays = days

    if (option.isBase) {
      this.supplySelection = null
      this.selectSellingPlan(option.plan.id)
    } else {
      this.supplySelection = option
      this.selectedSellingPlanGroup = this.availableSellingPlanGroups?.[0] || this.selectedSellingPlanGroup
      this.selectedSellingPlan = option.plan
    }
  }

  // Re-resolve the current supply selection after a variant change (flavor swap):
  // the bundle variant, allocation and prices are re-mapped from the new base variant.
  syncSupplySelection() {
    if (this.layoutMode !== 'supply') return

    if (this.selectedSupplyDays == null) {
      // Nothing chosen yet: honor ?supply= deep links, else mirror the default
      // subscription selection (if any) onto the matching supply row.
      const urlSupply = Number(new URL(window.location.href).searchParams.get('supply'))
      if (urlSupply && this.supplyOptions().some(option => option.days === urlSupply)) {
        this.selectSupply(urlSupply)
      } else if (this.selectedSellingPlan) {
        const days = this.planIntervalDays(this.selectedSellingPlan)
        const match = this.supplyOptions().find(option => option.days === days)
        this.selectSupply(match ? match.days : 30)
      }
      return
    }

    if (this.selectedSellingPlan) {
      // Re-map against the (possibly new) variant; if the chosen supply option no
      // longer resolves (e.g. bundle variant sold out for this flavor), fall back
      // rather than leaving a stale bundle selection behind.
      const options = this.supplyOptions()
      if (options.some(option => option.days === this.selectedSupplyDays)) {
        this.selectSupply(this.selectedSupplyDays)
      } else if (options.length) {
        this.selectSupply(options[0].days)
      } else {
        this.selectedSupplyDays = null
        this.supplySelection = null
      }
    } else {
      // Switched to one-time purchase.
      this.selectedSupplyDays = null
      this.supplySelection = null
    }
  }

  // The product/variant/plan the form will actually submit (bundle when a
  // 60/90-day option is selected). Consumed by the sticky bar and price displays.
  effectiveSelection() {
    const supply = this.layoutMode === 'supply' && this.supplySelection ? this.supplySelection : null
    const price = this.effectivePrice(false)

    return {
      productId: supply ? supply.product.id : this.product?.id,
      variantId: supply ? supply.variant.id : this.selectedVariant?.id,
      price,
      formattedPrice: this.money(price),
      comparePrice: supply ? supply.compare : this.selectedSellingPlan ? this.planComparePrice(this.selectedSellingPlan) : null,
      planLabel: this.selectedSellingPlan
        ? supply
          ? `${supply.days} Day Supply`
          : this.layoutMode === 'supply'
          ? this.planLabel(this.selectedSellingPlan).label
          : this.planLabel(this.selectedSellingPlan).sub || this.planLabel(this.selectedSellingPlan).label
        : null,
      isSubscription: !!this.selectedSellingPlan,
      supplyDays: this.selectedSupplyDays ?? null,
    }
  }

  effectivePrice(formatted = true) {
    if (this.layoutMode === 'supply' && this.supplySelection) {
      return formatted ? this.money(this.supplySelection.price) : this.supplySelection.price
    }
    if (this.selectedSellingPlan) return this.price(this.selectedSellingPlan, formatted)
    return formatted ? this.money(this.selectedVariant.price) : this.selectedVariant.price
  }

  // !SECTION: Supply layout

  tabsTemplate(groups, showOnetime) {
    const tabs = groups.map(group => this.groupTabTemplate(group))

    if (showOnetime) {
      if (this.options?.onetime_first) tabs.unshift(this.onetimeTabTemplate())
      else tabs.push(this.onetimeTabTemplate())
    }

    if (tabs.length < 2) return ''

    return html`<div class="plan-tabs" role="tablist">${tabs}</div>`
  }

  groupTabTemplate(group) {
    const on = this.selectedSellingPlanGroup == group
    return html`
      <button type="button" class="plan-tab ${on ? 'on' : ''}" role="tab" aria-selected=${on ? 'true' : 'false'} @click=${() => this.selectSellingPlan(group.selected_selling_plan.id)}>
        ${this.groupTitle(group)}
      </button>
    `
  }

  onetimeTabTemplate() {
    const on = !this.selectedSellingPlanGroup
    return html`
      <button type="button" class="plan-tab ${on ? 'on' : ''}" role="tab" aria-selected=${on ? 'true' : 'false'} @click=${() => this.selectSellingPlanGroup(null)}>
        ${this.options?.onetime_title ? this.options?.onetime_title : 'One time purchase'}
      </button>
    `
  }

  optionTemplate({ selected, best, label, sub, was, now, tag, onSelect }) {
    return html`
      <label class="opt-stack ${selected ? 'on' : ''}">
        ${best ? html`<span class="best-pill">Best Value</span>` : ''}
        <input class="sr-only" type="radio" name="skio-option-${this.key}" .checked=${selected} @change=${onSelect} />
        <span class="radio" aria-hidden="true"></span>
        <span class="opt-main">
          <span class="opt-label">${label}</span>
          ${sub ? html`<span class="opt-sub">${sub}</span>` : ''}
        </span>
        <span class="opt-price ${was ? '' : 'no-was'}" aria-live="polite">
          ${was ? html`<s class="was" aria-hidden="true">${was}</s>` : ''}
          <span class="now">${now}</span>
          ${tag ? html`<span class="tag">${tag}</span>` : ''}
        </span>
      </label>
    `
  }

  groupPanelTemplate(group) {
    const on = this.selectedSellingPlanGroup == group
    const best = this.bestValuePlan(group)

    return html`
      <div class="tab-panel ${on ? 'on' : ''}" role="tabpanel">
        <div class="options-stack">
          ${this.getAvailableSellingPlans(group).map(selling_plan => {
            const { label, sub } = this.planLabel(selling_plan)
            const was = this.planComparePrice(selling_plan)

            return this.optionTemplate({
              selected: on && group.selected_selling_plan?.id == selling_plan.id,
              best: best === selling_plan,
              label,
              sub,
              was: was ? this.money(was) : null,
              now: this.price(selling_plan),
              tag: this.discountText(selling_plan),
              onSelect: () => this.selectSellingPlan(selling_plan.id),
            })
          })}
        </div>

        ${this.benefitsTemplate(group)}
        ${this.additionalContentText()}
      </div>
    `
  }

  // Tabs for the supply layout: the subscription tab re-selects the last picked
  // supply option instead of a raw selling plan.
  supplyTabsTemplate(groups, showOnetime) {
    const group = groups[0]
    const on = !!this.selectedSellingPlanGroup

    const tabs = [
      html`
        <button type="button" class="plan-tab ${on ? 'on' : ''}" role="tab" aria-selected=${on ? 'true' : 'false'} @click=${() => this.selectSupply(this._lastSupplyDays || 30)}>
          ${this.groupTitle(group)}
        </button>
      `,
    ]

    if (showOnetime) {
      if (this.options?.onetime_first) tabs.unshift(this.onetimeTabTemplate())
      else tabs.push(this.onetimeTabTemplate())
    }

    if (tabs.length < 2) return ''

    return html`<div class="plan-tabs" role="tablist">${tabs}</div>`
  }

  // Subscription panel for the supply layout: one row per 30/60/90-day option.
  // Visually identical to the classic rows; 60/90 rows are backed by bundle products.
  supplyPanelTemplate(group) {
    const on = !!this.selectedSellingPlanGroup
    const options = this.supplyOptions()
    const best = this.bestSupplyOption(options)

    return html`
      <div class="tab-panel ${on ? 'on' : ''}" role="tabpanel">
        <div class="options-stack">
          ${options.map(option => {
            const was = option.isBase ? option.compare : this.supplyComparePrice(option)

            return this.optionTemplate({
              selected: on && this.selectedSupplyDays === option.days,
              best: best === option,
              label: `${option.days} Day Supply`,
              sub: `Every ${option.days} days`,
              was: was ? this.money(was) : null,
              now: this.money(option.isBase ? this.price(option.plan, false) : option.price),
              tag: option.isBase ? this.discountText(option.plan) : this.supplyDiscountText(option),
              onSelect: () => this.selectSupply(option.days),
            })
          })}
        </div>

        ${this.benefitsTemplate(group)}
        ${this.additionalContentText()}
      </div>
    `
  }

  // Subscribe & Save two-card layout (products without supply bundles).
  cardsTemplate(groups, showOnetime) {
    const group = groups[0]
    const plans = group ? this.getAvailableSellingPlans(group) : []
    const subOn = !!this.selectedSellingPlanGroup
    const plan = (subOn && this.selectedSellingPlan && plans.find(p => p.id === this.selectedSellingPlan.id)) || group?.selected_selling_plan || plans[0]
    if (!plan) return ''

    const discount = this.discount(plan)
    const was = this.planComparePrice(plan)
    const frequency = this.planLabel(plan).sub.replace(/^Every/, 'every')

    const variant = this.selectedVariant
    const onetimeWas = this.options?.show_compare_price && variant.compare_at_price > variant.price ? variant.compare_at_price : null

    return html`
      <div class="ss-cards">
        <label class="ss-card ss-card--sub ${subOn ? 'on' : ''}">
          <input class="sr-only" type="radio" name="skio-card-${this.key}" .checked=${subOn} @change=${() => this.selectSellingPlan(plan.id)} />
          ${discount.percent > 0 ? html`<span class="ss-badge ss-badge--save">Save ${discount.percent}%</span>` : ''}
          <span class="ss-card-title">${this.groupTitle(group)}</span>
          <span class="ss-card-price">
            <span class="now">${this.price(plan)}</span>
            ${was ? html`<s class="was" aria-hidden="true">${this.money(was)}</s>` : ''}
          </span>
          <span class="ss-freq-line">
            Shipped
            <button type="button" class="ss-freq" aria-expanded=${this.freqOpen ? 'true' : 'false'} @click=${e => { e.stopPropagation(); this.selectSellingPlan(plan.id); this.freqOpen = !this.freqOpen }}>
              ${frequency}
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" /></svg>
            </button>
          </span>
          ${this.freqOpen && plans.length > 1
            ? html`
                <div class="ss-freq-menu" role="listbox">
                  ${plans.map(
                    p => html`
                      <button type="button" role="option" class="ss-freq-opt ${p.id === plan.id ? 'on' : ''}" aria-selected=${p.id === plan.id ? 'true' : 'false'} @click=${e => {
                        e.stopPropagation()
                        this.freqOpen = false
                        this.selectSellingPlan(p.id)
                      }}>
                        ${this.planLabel(p).sub}
                      </button>
                    `
                  )}
                </div>
              `
            : ''}
          ${discount.percent > 0 ? html`<span class="ss-badge ss-badge--best">Best Value</span>` : ''}
        </label>

        ${showOnetime
          ? html`
              <label class="ss-card ss-card--onetime ${!subOn ? 'on' : ''}">
                <input class="sr-only" type="radio" name="skio-card-${this.key}" .checked=${!subOn} @change=${() => { this.freqOpen = false; this.selectSellingPlanGroup(null) }} />
                <span class="ss-card-title">${this.options?.onetime_title ? this.options.onetime_title : 'One-Time Purchase'}</span>
                <span class="ss-card-price">
                  <span class="now">${this.money(variant.price)}</span>
                  ${onetimeWas ? html`<s class="was" aria-hidden="true">${this.money(onetimeWas)}</s>` : ''}
                </span>
              </label>
            `
          : ''}
      </div>

      ${this.benefitsTemplate(group)}
      ${this.additionalContentText()}
    `
  }

  onetimePanelTemplate(groups) {
    const on = !this.selectedSellingPlanGroup
    const variant = this.selectedVariant
    const was = this.options?.show_compare_price && variant.compare_at_price > variant.price ? variant.compare_at_price : null

    let tag = ''
    if (was && this.options?.discount_text) {
      const percent = Math.round(((was - variant.price) / was) * 100)
      if (percent > 0) {
        tag = unsafeHTML(
          this.options.discount_text
            .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(was - variant.price) : percent + '%')
            .replaceAll('[future_price_adjustments]', '')
        )
      }
    }

    const label = variant.title && variant.title !== 'Default Title' ? variant.title : this.options?.onetime_title ? this.options?.onetime_title : 'One time purchase'

    return html`
      <div class="tab-panel ${on ? 'on' : ''}" role="tabpanel">
        <div class="options-stack">
          ${this.optionTemplate({
            selected: on,
            best: false,
            label,
            sub: 'Single purchase',
            was: was ? this.money(was) : null,
            now: this.money(variant.price),
            tag,
            onSelect: () => this.selectSellingPlanGroup(null),
          })}
        </div>

        ${groups.length ? this.upsellTemplate(groups) : ''}
      </div>
    `
  }

  benefitsTemplate(group) {
    const discount = this.discount(group.selected_selling_plan)
    const amountText = this.options?.discount_format === 'absolute' && discount.absolute ? this.money(discount.absolute) : discount.percent ? discount.percent + '%' : ''

    return html`
      <div class="benefits">
        <div class="benefit">
          <span class="bi">${this.piggyIcon()}</span>
          <p>${amountText ? amountText + ' off every subscription order' : 'Save on every subscription order'}</p>
        </div>
        <div class="benefit">
          <span class="bi">${this.certificateIcon()}</span>
          <p>Exclusive special offers</p>
        </div>
        <div class="benefit">
          <span class="bi">${this.pauseIcon()}</span>
          <p>Pause or cancel at any time</p>
        </div>
      </div>
    `
  }

  upsellTemplate(groups) {
    const max = this.maxDiscountPercent()
    if (!max) return ''

    return html`
      <div class="upsell-card">
        <div class="upsell-headline">Subscribe and save up to ${max}%</div>
        <button type="button" class="upsell-switch" @click=${() => this.selectSellingPlan(groups[0].selected_selling_plan.id)}>Switch & Save</button>
      </div>
    `
  }

  invalidIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#D22B2B" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  validIcon() {
    return html`
      <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="#0BDA51" class="w-6 h-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `
  }

  setupMode() {
    return html`
      <div class="setupMode">
        Skio Plan Picker
        <small>This will not appear on the live site.</small>

        <small>Key: ${this.key}</small>

        <div class="detection">${this.product ? this.validIcon() : this.invalidIcon()} Product</div>

        <div class="detection">${this.selectedVariant ? this.validIcon() : this.invalidIcon()} Selected Variant</div>

        <div class="detection">${this.form ? this.validIcon() : this.invalidIcon()} Form</div>

        <div class="detection">${this.variantInput ? this.validIcon() : this.invalidIcon()} Variant Input</div>

        <div class="detection">
          ${this.variantChanged ? this.validIcon() : this.invalidIcon()} Variant Change Detection
          <small>Please attempt to change the selected variant.</small>
        </div>

        <div class="detection">
          Layout: ${this.layoutMode}
          <small>'supply' = 30/60/90 day bundle products, 'cards' = Subscribe &amp; Save cards.</small>
        </div>

        ${(this.supplyProducts || []).map(bundle => {
          const variant = this.mapBundleVariant(bundle)
          return html`
            <div class="detection">${bundle?.available ? this.validIcon() : this.invalidIcon()} ${bundle?.days} Day Supply product (${bundle?.title || 'missing'})</div>
            <div class="detection">${variant ? this.validIcon() : this.invalidIcon()} ${bundle?.days} Day Supply variant mapping</div>
          `
        })}

        <small>If any of these don't pass please troubleshoot using our <a target="_blank" rel="noreferrer" href="https://integrate.skio.com/troubleshooting">troubleshooting guide</a>.</small>
      </div>
    `
  }

  showDetails() {
    return html`
      <details class="skio-details" @mouseover=${e => this.detailsMouseover()} @mouseleave=${e => this.detailsMouseleave()}>
        <summary>
          <span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="ai ai-ArrowRepeat"
            >
              <path d="M18 2l3 3-3 3" />
              <path d="M6 22l-3-3 3-3" />
              <path d="M21 5H10a7 7 0 0 0-7 7" />
              <path d="M3 19h11a7 7 0 0 0 7-7" />
            </svg>

            How do subscriptions work?
          </span>
        </summary>
        <div class="skio-details--content">
          <ul>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div class="skio-content">
                <p>Get exclusive deals</p>
                <small>Subscribe for unique discounts</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
              <div class="skio-content">
                <p>Edit your subscription anytime</p>
                <small>Edit products, delivery schedule and more</small>
              </div>
            </li>
            <li>
              <div class="skio-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>

              <div class="skio-content">
                <p>No commitment</p>
                <small>Easy to cancel if it's not for you</small>
              </div>
            </li>
          </ul>
          <div class="skio-details--footer">
            <a class="skio-manage-link" href="/a/account/login">Manage subscriptions</a>

            <a style="letter-spacing: 0" class="powered-by-skio" href="https://skio.com/?utm_source=eonsincshop.myshopify.com&utm_medium=details_popover" target="_blank" rel="noopener">
              Powered by
              <svg width="24" height="11" viewBox="0 0 24 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.28399 5.78801C4.12399 5.63601 3.93599 5.50801 3.71999 5.40401C3.50399 5.30001 3.27599 5.24801 3.03599 5.24801C2.85199 5.24801 2.67999 5.28401 2.51999 5.35601C2.36799 5.42801 2.29199 5.55201 2.29199 5.72801C2.29199 5.89601 2.37599 6.01601 2.54399 6.08801C2.71999 6.16001 2.99999 6.24001 3.38399 6.32801C3.60799 6.37601 3.83199 6.44401 4.05599 6.53201C4.28799 6.62001 4.49599 6.73601 4.67999 6.88001C4.86399 7.02401 5.01199 7.20001 5.12399 7.40801C5.23599 7.61601 5.29199 7.86401 5.29199 8.15201C5.29199 8.52801 5.21599 8.84801 5.06399 9.11201C4.91199 9.36801 4.71199 9.57601 4.46399 9.73601C4.22399 9.89601 3.95199 10.012 3.64799 10.084C3.34399 10.156 3.03999 10.192 2.73599 10.192C2.24799 10.192 1.76799 10.116 1.29599 9.96401C0.831989 9.80401 0.443989 9.57201 0.131989 9.26801L1.23599 8.10401C1.41199 8.29601 1.62799 8.45601 1.88399 8.58401C2.13999 8.71201 2.41199 8.77601 2.69999 8.77601C2.85999 8.77601 3.01599 8.74001 3.16799 8.66801C3.32799 8.58801 3.40799 8.45201 3.40799 8.26001C3.40799 8.07601 3.31199 7.94001 3.11999 7.85201C2.92799 7.76401 2.62799 7.67201 2.21999 7.57601C2.01199 7.52801 1.80399 7.46401 1.59599 7.38401C1.38799 7.30401 1.19999 7.19601 1.03199 7.06001C0.871989 6.92401 0.739989 6.75601 0.635989 6.55601C0.531989 6.35601 0.479989 6.11601 0.479989 5.83601C0.479989 5.47601 0.555989 5.17201 0.707989 4.92401C0.859989 4.66801 1.05599 4.46001 1.29599 4.30001C1.53599 4.14001 1.79999 4.02401 2.08799 3.95201C2.38399 3.87201 2.67599 3.83201 2.96399 3.83201C3.41199 3.83201 3.84799 3.90401 4.27199 4.04801C4.70399 4.18401 5.06799 4.39201 5.36399 4.67201L4.28399 5.78801Z"
                  fill="black"
                />
                <path d="M12.8481 10H10.4121L8.45615 7.13201H8.42015V10H6.44015V0.928009H8.42015V6.44801H8.45615L10.3641 4.02401H12.7521L10.4481 6.72401L12.8481 10Z" fill="black" />
                <path
                  d="M15.7009 2.11601C15.7009 2.26801 15.6689 2.41201 15.6049 2.54801C15.5489 2.67601 15.4689 2.78801 15.3649 2.88401C15.2689 2.98001 15.1489 3.05601 15.0049 3.11201C14.8689 3.16801 14.7249 3.19601 14.5729 3.19601C14.2529 3.19601 13.9849 3.09201 13.7689 2.88401C13.5529 2.66801 13.4449 2.41201 13.4449 2.11601C13.4449 1.97201 13.4729 1.83601 13.5289 1.70801C13.5849 1.57201 13.6649 1.45601 13.7689 1.36001C13.8729 1.26401 13.9929 1.18801 14.1289 1.13201C14.2649 1.06801 14.4129 1.03601 14.5729 1.03601C14.7249 1.03601 14.8689 1.06401 15.0049 1.12001C15.1489 1.17601 15.2689 1.25201 15.3649 1.34801C15.4689 1.44401 15.5489 1.56001 15.6049 1.69601C15.6689 1.82401 15.7009 1.96401 15.7009 2.11601ZM13.5889 10V4.02401H15.5569V10H13.5889Z"
                  fill="black"
                />
                <path
                  d="M23.4516 6.98801C23.4516 7.47601 23.3636 7.92001 23.1876 8.32001C23.0116 8.71201 22.7716 9.04801 22.4676 9.32801C22.1636 9.60001 21.8116 9.81201 21.4116 9.96401C21.0116 10.116 20.5836 10.192 20.1276 10.192C19.6796 10.192 19.2516 10.116 18.8436 9.96401C18.4436 9.81201 18.0916 9.60001 17.7876 9.32801C17.4916 9.04801 17.2556 8.71201 17.0796 8.32001C16.9036 7.92001 16.8156 7.47601 16.8156 6.98801C16.8156 6.50001 16.9036 6.06001 17.0796 5.66801C17.2556 5.27601 17.4916 4.94401 17.7876 4.67201C18.0916 4.40001 18.4436 4.19201 18.8436 4.04801C19.2516 3.90401 19.6796 3.83201 20.1276 3.83201C20.5836 3.83201 21.0116 3.90401 21.4116 4.04801C21.8116 4.19201 22.1636 4.40001 22.4676 4.67201C22.7716 4.94401 23.0116 5.27601 23.1876 5.66801C23.3636 6.06001 23.4516 6.50001 23.4516 6.98801ZM21.5556 6.98801C21.5556 6.79601 21.5236 6.60801 21.4596 6.42401C21.3956 6.24001 21.3036 6.08001 21.1836 5.94401C21.0636 5.80001 20.9156 5.68401 20.7396 5.59601C20.5636 5.50801 20.3596 5.46401 20.1276 5.46401C19.8956 5.46401 19.6916 5.50801 19.5156 5.59601C19.3396 5.68401 19.1916 5.80001 19.0716 5.94401C18.9596 6.08001 18.8716 6.24001 18.8076 6.42401C18.7516 6.60801 18.7236 6.79601 18.7236 6.98801C18.7236 7.18001 18.7516 7.36801 18.8076 7.55201C18.8716 7.73601 18.9636 7.90401 19.0836 8.05601C19.2036 8.20001 19.3516 8.31601 19.5276 8.40401C19.7036 8.49201 19.9076 8.53601 20.1396 8.53601C20.3716 8.53601 20.5756 8.49201 20.7516 8.40401C20.9276 8.31601 21.0756 8.20001 21.1956 8.05601C21.3156 7.90401 21.4036 7.73601 21.4596 7.55201C21.5236 7.36801 21.5556 7.18001 21.5556 6.98801Z"
                  fill="black"
                />
              </svg>
            </a>
          </div>
        </div>
      </details>
    `
  }

  // !SECTION: Element Templates

  render() {
    if (!this.viable() || this.options?.layout === 'hidden') {
      return this.debug ? this.setupMode() : ''
    }

    if (this.selectedVariant.selling_plan_allocations.length === 0 && !this.options?.show_without_subscription) {
      return this.debug ? this.setupMode() : ''
    }

    if (this.availableSellingPlanGroups?.length == 0 && !this.options?.show_without_subscription) return

    const groups = this.availableSellingPlanGroups || []
    const showOnetime = !this.product.requires_selling_plan

    let body
    if (groups.length && this.layoutMode === 'supply') {
      body = html`
        ${this.supplyTabsTemplate(groups, showOnetime)}

        <div class="tab-stack">
          ${this.supplyPanelTemplate(groups[0])}
          ${showOnetime ? this.onetimePanelTemplate(groups) : ''}
        </div>
      `
    } else if (groups.length && this.layoutMode === 'cards') {
      body = this.cardsTemplate(groups, showOnetime)
    } else {
      body = html`
        ${this.tabsTemplate(groups, showOnetime)}

        <div class="tab-stack">
          ${groups.map(group => this.groupPanelTemplate(group))}
          ${showOnetime ? this.onetimePanelTemplate(groups) : ''}
        </div>
      `
    }

    return html`
      ${this.debug ? this.setupMode() : null}

      <fieldset class="skio-plan-picker" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend" class="${!this.options?.show_legend ? 'sr-only' : ''}">${unsafeHTML(this.options?.legend_content)}</legend>

        ${body}

        ${this.options?.show_details == true ? this.showDetails() : ''}
      </fieldset>
    `
  }

  async fetchRules() {
    if (sessionStorage.getItem('skio-discount-rules')) {
      return JSON.parse(sessionStorage.getItem('skio-discount-rules'))
    } else {
      const rules = await fetch(`https://api.skio.com/storefront-http/get-rules-by-domain-or-hostname?domain=${window?.Shopify?.shop}`)
        .then(response => response.json())
        .then(response => {
          return response.rules.filter(rule => rule.type !== 'surpriseDelight').sort((a, b) => a.minQuantityToDiscount - b.minQuantityToDiscount)
        })

      sessionStorage.setItem('skio-discount-rules', JSON.stringify(rules))
      return rules
    }
  }

  eligibleItemCount(rule) {
    const eligibleQuantity = rule?.productVariantIds.some(gid => gid.includes(this.selectedVariant.id)) ? this.quantity : 0

    if (!this.cart?.items) return eligibleQuantity

    const cartItems = Number(
      this.cart?.items.reduce(
        (aggregate, item) =>
          rule?.productVariantIds.some(gid => gid.includes(item.id) && item.selling_plan_allocation && gid.includes(this.selectedVariant.id)) ? aggregate + item.quantity : aggregate,
        0
      )
    )

    return cartItems + eligibleQuantity
  }

  getRule() {
    let largestMinQuantity = {
      value: -1,
      index: -1,
    }

    this.rules.forEach((rule, index) => {
      if (rule.code && largestMinQuantity.value < rule.minQuantityToDiscount) {
        if (rule.minQuantityToDiscount <= this.eligibleItemCount(rule)) {
          largestMinQuantity.value = rule.minQuantityToDiscount
          largestMinQuantity.index = index
        }
      }
    })

    this.rule = largestMinQuantity.index > -1 ? this.rules[largestMinQuantity.index] : null
  }

  bindCartEvents() {
    document.addEventListener('CartUpdated', event => {
      this.cart = event.detail
    })
  }

  bindFormEvents() {
    // First try to find form by specified form_id
    this.form = document.querySelector(`#${this.options?.form_id}`)
    
    // If no form found by ID, search through parents and page
    if (!this.form) {
      this.form = this.findProductForm()
    }

    if (!this.form) {
      this.error('No valid product form found')
      return
    }

    // Add debug styling if debug mode is enabled
    if (this.debug) {
      this.form.style.borderStyle = 'solid'
      this.form.style.borderWidth = '4px'
      this.form.style.borderImage = 'repeating-linear-gradient(-55deg,#000,#000 20px,#ffb101 20px,#ffb101 40px)10'
    }

    // Ensure form has an ID
    if (!this.form.getAttribute('id')) {
      this.form.setAttribute('id', `skio-form-${this.product.id}`)
    }

    // Set form attribute on selling plan input
    const sellingPlanInput = this.querySelector('input[name="selling_plan"]')
    if (sellingPlanInput) {
      sellingPlanInput.setAttribute('form', this.form.getAttribute('id'))
    }

    this.variantInput = this.form.querySelector('[name="id"]')
    if (!this.variantInput) this.variantInput = document.querySelector(`[name="id"][form="${this.options?.form_id}"]`)

    if (this.variantInput) {
      this.variantInput.addEventListener('change', e => {
        this.selectedVariant = this.product.variants.find(variant => variant.id == e.target.value)
        this.variantChanged = true
      })
      // this.form.addEventListener('change', e => {
      //   this.selectedVariant = this.product.variants.find(variant => variant.id == this.variantInput.value)
      //   this.variantChanged = true
      // })
    }

    const $quantityInput = this.form.querySelector('[name="quantity"]')

    if ($quantityInput) {
      this.quantity = Number($quantityInput.value)
      $quantityInput.addEventListener('input', () => {
        this.quantity = Number($quantityInput.value)
      })
    }

    // Supply layout submit-time swap: [name="id"] always holds the base variant
    // (the theme's ProductFormReader rewrites it on every option/plan/qty change,
    // so storing the bundle id there is unwinnable). Instead, swap in the bundle
    // variant id only for the synchronous instant the add-to-cart click builds
    // FormData, then restore. Capture phase runs before the theme's AJAX
    // add-to-cart click handler. No 'change' is dispatched, so no listeners react.
    if (this._captureAdd) {
      document.removeEventListener('click', this._captureAdd, true)
      document.removeEventListener('submit', this._captureAdd, true)
    }
    this._captureAdd = e => {
      if (!this.supplySelection || !this.variantInput) return
      const target = e.target instanceof Element ? e.target : null
      const withinForm = e.type === 'submit' ? e.target === this.form : !!target?.closest('[data-add-to-cart], [type="submit"], button[name="add"]') && target.closest('form') === this.form
      if (!withinForm) return

      const idInput = this.variantInput
      const baseId = idInput.value
      idInput.value = this.supplySelection.variant.id
      setTimeout(() => {
        idInput.value = baseId
      }, 0)
    }
    document.addEventListener('click', this._captureAdd, true)
    // Backstop for native (non-AJAX) form submission.
    document.addEventListener('submit', this._captureAdd, true)

    let skio = this
    // Example variantChanged event dispatch:
    // document.dispatchEvent( new CustomEvent("variantChanged", { detail: { variantId: variant.id } }) );
    document.addEventListener('variantChanged', function (e) {
      let variantId = e.detail.variantId
      let variant = skio.product.variants.find(x => x.id == variantId)
      if (variant) skio.selectedVariant = variant
      else skio.log('Unable to find variant with id: ', variantId)
      this.variantChanged = true
      skio.requestUpdate()
    })
  }

  findProductForm() {
    // First try finding form through parent elements
    const formFromParent = this.findFormThroughParents()
    if (formFromParent) return formFromParent

    // If no form found through parents, look for any valid product forms on page
    const allProductForms = this.getAllProductForms()
    return allProductForms[0] || null
  }

  findFormThroughParents() {
    let currentElement = this
    let depth = 0
    const maxDepth = 5

    while (currentElement.parentElement && depth < maxDepth) {
      const forms = Array.from(currentElement.querySelectorAll('form'))
      const validForm = forms.find(form => this.isValidProductForm(form))
      
      if (validForm) return validForm

      currentElement = currentElement.parentElement
      depth++
    }

    return null
  }

  getAllProductForms() {
    return Array.from(document.forms).filter(form => {
      const hasVariantId = this.getVariantId(form)
      return this.isValidProductForm(form) && hasVariantId
    })
  }

  isValidProductForm(form) {
    const hasCartAction = form.getAttribute('action')?.includes('/cart/add')
    const hasSubmitButton = !!form.querySelector('button, [type="submit"]')
    return hasCartAction && hasSubmitButton
  }

  getVariantId(form) {
    // Try getting ID from FormData first
    const formData = new FormData(form)
    const idFromFormData = formData.get('id')
    if (idFromFormData) return Number(idFromFormData)

    // Fallback to finding input element
    const idInput = form.querySelector('[name="id"]')
    return idInput ? Number(idInput.value) : null
  }

  detailsMouseover() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (!details.hasAttribute('open') && this.showDetailsHover == false) {
      summary.click()
      this.showDetailsHover = true
    }
  }

  detailsMouseleave() {
    let details = this.renderRoot.querySelector('.skio-details')
    let summary = this.renderRoot.querySelector('.skio-details summary')
    if (details.hasAttribute('open') && this.showDetailsHover == true) {
      summary.click()
      this.showDetailsHover = false
    }
  }

  updated(changed) {
    if (!this.viable()) return

    if (changed.has('product') && this.product) {
      this.key = this.key ? this.key : this.product.id
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      const filteredSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group =>
          selling_plan_group.app_id === 'SKIO' && !selling_plan_group.name.toLowerCase().includes('hidden-') && !selling_plan_group.name.toLowerCase().includes('dynamic box subscription')
      )

      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = filteredSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
        )
      )

      if (this.options?.combine_groups) {
        this.availableSellingPlanGroups = [
          {
            name: this.options?.combined_group_name,
            selling_plans: this.availableSellingPlanGroups.flatMap(group => group.selling_plans),
            id: 'combined_group',
            app_id: 'SKIO',
          },
        ]
      }

      // TODO: Select proper group depending on what selling plan is selected

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length) {
        const url = new URL(window.location.href)
        const urlSelectedSellingPlanId = url.searchParams.get('selling_plan')

        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach(group => {
          const availableSellingPlans = this.getAvailableSellingPlans(group)

          const urlSelectedSellingPlan = availableSellingPlans.find(plan => plan.id == urlSelectedSellingPlanId)
          const nameSelectedSellingPlan = availableSellingPlans.find(plan => plan.name === this.selectedSellingPlan?.name)
          const defaultSellingPlan =
            this.options?.default_subscription && this.options.default_subscription.trim() !== ''
              ? availableSellingPlans.find(plan => plan.name.toLowerCase().includes(this.options.default_subscription.toLowerCase()))
              : null

          group.selected_selling_plan = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan || availableSellingPlans[0]

          const isAnyPlanSelected = urlSelectedSellingPlan || nameSelectedSellingPlan || defaultSellingPlan

          group.selected = !!isAnyPlanSelected
        })

        if ((!this.variantChanged && this.options?.start_onetime == false) || this.product.requires_selling_plan == true || urlSelectedSellingPlanId || this.selectedSellingPlan) {
          let selectedSellingPlanGroup = this.availableSellingPlanGroups.find(group => group.selected) || this.availableSellingPlanGroups[0]
          let selectedSellingPlanId = selectedSellingPlanGroup.selected_selling_plan.id
          this.selectSellingPlan(selectedSellingPlanId)
        } else {
          this.selectSellingPlanGroup(null)
        }
      } else {
        this.selectSellingPlanGroup(null)
      }

      // Supply layout: re-map the selected 30/60/90 option against the new
      // variant (and honor ?supply= deep links on first resolution).
      this.syncSupplySelection()
    }

    if (changed.has('selectedSellingPlan')) {
      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
          formId: this.form?.getAttribute('id') || null,
          // What the form will actually submit (bundle product/variant when a
          // 60/90-day supply option is selected) — used by the sticky bar.
          effective: this.effectiveSelection(),
        },
      })

      this.dispatchEvent(event)

      this.dispatchEvent(
        new CustomEvent('change', {
          detail: { value: this.selectedSellingPlan?.id },
        })
      )
    }

    this.updateForm()
    this.updateExternalPrice()

    if (!this.disableUrl) {
      this.updateURLParams()
    }

    if (changed.has('quantity')) {
      this.getRule()
    }

    if (changed.has('cart')) {
      this.getRule()
    }

    this.updateExternalElements()
  }

  updateExternalElements() {
    document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach(el => {
      el.innerHTML = this.effectivePrice()
    })

    document.querySelectorAll(`[skio-subscription-content]`).forEach(el => {
      el.style.display = this.selectedSellingPlan ? 'block' : 'none'
    })

    const addToCartText = document.querySelector('[data-add-to-cart-text]')
    if (addToCartText) addToCartText.innerHTML = this.selectedSellingPlan ? 'Subscribe & Save' : 'Add to Cart'

    // The theme only knows base-product prices; keep the in-button price in
    // sync with the effective (possibly bundle) selection.
    const buttonPrice = this.form?.querySelector('[data-button-price]')
    if (buttonPrice) buttonPrice.innerHTML = this.effectivePrice()
  }

  // SECTION: Utility Functions
  log(...args) {
    args.unshift(`%c[skio plan picker][${this.key}]`, 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error(...args) {
    args.unshift(`%c [skio plan picker][${this.key}]`, 'color: #ff0000')
    console.error.apply(console, args)
  }

  appendOrdinalSuffix(num) {
    const j = num % 10,
      k = num % 100
    if (j == 1 && k != 11) {
      return num + 'st'
    }
    if (j == 2 && k != 12) {
      return num + 'nd'
    }
    if (j == 3 && k != 13) {
      return num + 'rd'
    }
    return num + 'th'
  }

  viable() {
    const errors = []

    const sellingPlanInput = this.querySelector('input[name=selling_plan]')
    if (!sellingPlanInput) {
      errors.push('Missing selling plan input')
    }

    if (!this.product) {
      errors.push('No product found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.selectedVariant) {
      errors.push('No variant found. Please pass a product or productHandle to the skio-plan-picker component.')
    }
    if (!this.form) {
      errors.push('No form found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id".')
    }
    if (!this.variantInput) {
      errors.push('No variant input found. Please add a form_id to the plan picker through the Shopify customizer or ensure that the element is in a form that has an input with name="id"')
    }

    if (errors.length == 0) return true

    this.error('Errors', errors)
    return false
  }

  getAvailableSellingPlans(group) {
    let availableSellingPlans = group.selling_plans.filter(selling_plan =>
      this.selectedVariant.selling_plan_allocations.some(selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id)
    )

    return availableSellingPlans
  }

  // !SECTION: Utility Functions

  // Update selected selling plan group; called on click of group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan

    if (!group) {
      // One-time purchase: always submit the base variant.
      this.supplySelection = null
      this.selectedSupplyDays = null
    }
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(selling_plan_id) {
    const selectedGroup = this.availableSellingPlanGroups.find(group => group.selling_plans.some(plan => plan.id == selling_plan_id))
    const selectedSellingPlan = selectedGroup.selling_plans.find(plan => plan.id == selling_plan_id)

    selectedGroup.selected_selling_plan = selectedSellingPlan
    this.selectedSellingPlanGroup = selectedGroup
    this.selectedSellingPlan = selectedSellingPlan
  }

updateExternalPrice() {
  document.querySelectorAll(this.options?.external_price_selector).forEach(el => {
    const price = this.effectivePrice();
    // Check if the product is on sale by comparing compare_at_price and price
    const isOnSale = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > this.selectedVariant.price;
    // Only show badge if a subscription plan is selected and the product is not on sale
    const badge = this.selectedSellingPlan && !isOnSale
      ? '<span class="subscription-badge">Subscription Price</span>'
      : '';
    el.innerHTML = `${price}${badge}`;
  });
}

  additionalFrequencyLabel() {
    console.log(this.options?.additional_frequency_label)

    return unsafeHTML(this.options?.additional_frequency_label)
  }

  additionalContentText() {
    if (!this.options?.additional_subscription_content || this.options?.additional_subscription_content == '') return

    return html`
      <div class="group-content-additional">
        ${unsafeHTML(
          this.options?.additional_subscription_content
            .replaceAll(
              '[discount]',
              this.options?.discount_format === 'absolute' ? this.money(this.discount(this.selectedSellingPlan).absolute) : this.discount(this.selectedSellingPlan).percent + '%'
            )
            .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(this.selectedSellingPlan) || '')
        )}
      </div>
    `
  }

  // SECTION: Discount Functions
  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discountText(selling_plan) {
    const discount = this.discount(selling_plan)
    const hasInvalidDiscount = Object.values(discount).some(value => value === 0 || value === Infinity || value.toString().includes('-'))

    if (hasInvalidDiscount) {
      return '' // Return empty if any invalid condition is met
    } else {
      return unsafeHTML(
        this.options?.discount_text
          .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
          .replaceAll('[future_price_adjustments]', this.postCheckoutDiscountsText(selling_plan) || '')
      )
    }
  }

  discount(selling_plan) {
    if (!selling_plan) return { percent: 0, amount: 0 }
    return this.getDiscountFromPriceAdjustment(selling_plan, selling_plan.price_adjustments[0])
  }

  postCheckoutPriceAdjustments(selling_plan) {
    const { price_adjustments } = selling_plan
    const postCheckoutPriceAdjustments = price_adjustments.filter(({ position }) => position !== 1)
    const discounts = postCheckoutPriceAdjustments.map(price_adjustment => this.getDiscountFromPriceAdjustment(selling_plan, price_adjustment))
    return discounts
  }

  postCheckoutDiscountsText(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan || !this.options?.future_price_adjustments_text || this.future_price_adjustments_text == '') return

    const postCheckoutPriceAdjustments = this.postCheckoutPriceAdjustments(sellingPlan)
    if (!postCheckoutPriceAdjustments.length) return

    return postCheckoutPriceAdjustments.map((discount, index) =>
      this.options?.future_price_adjustments_text
        .replaceAll('[discount]', this.options?.discount_format === 'absolute' ? this.money(discount.absolute) : discount.percent + '%')
        .replaceAll('[order_count]', index + 1)
        .replaceAll('[order_count_ordinal]', this.appendOrdinalSuffix(index + 1))
    )
  }

  getDiscountFromPriceAdjustment(selling_plan, price_adjustment) {
    const discount = {
      percent: 0,
      amount: 0,
      absolute: 0,
    }

    let multiplier = 1

    let sellingPlanGroup = this.product.selling_plan_groups.find(group => group.selling_plans.some(plan => plan.id == selling_plan.id))

    if (sellingPlanGroup?.name === 'Prepaid') {
      const str = selling_plan.name // replace with your string
      const intervalDate = /\b\d+\s*(days|weeks|months|years)\b/gi
      const intervalDateMatches = str.match(intervalDate)

      if (intervalDateMatches?.length) {
        const intervals = intervalDateMatches[0].match(/\d+/g)
        multiplier = intervals[0] / (intervals[1] || 1)
        multiplier = multiplier > 1 ? multiplier : 1
      }
    }

    const price = this.selectedVariant.price
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > price ? this.selectedVariant.compare_at_price : price

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = price_adjustment.value
        discount.absolute = Math.round((price * price_adjustment.value) / 100.0)
        discount.amount = Math.round((price * price_adjustment.value) / 100.0)
        break
      case 'fixed_amount':
        discount.percent = Math.round(((price_adjustment.value * 1.0) / price) * 100.0)
        discount.absolute = price_adjustment.value
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = Math.round((((compareAtPrice * multiplier - price_adjustment.value) * 1.0) / (compareAtPrice * multiplier)) * 100.0)
        discount.absolute = compareAtPrice * multiplier - price_adjustment.value
        discount.amount = price - price_adjustment.value
        break
    }

    return discount
  }

  /**
   * Formats a number of cents into a currency string using Shopify format string.
   *
   * https://help.shopify.com/en/manual/international/pricing/currency-formatting#currency-formatting-options
   *
   * Originally from: https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
   *
   * @param cents - The number of cents to format.
   * @param formatString - The format string to use.
   * @returns The formatted currency string.
   */
  money(cents, formatString = this.moneyFormat) {
    const placeholderRegex = /{{\s*(\w+)\s*}}/

    /**
     * Formats a number of cents into a currency string using the provided
     * precision, thousands separator, and decimal separator.
     * @param number - The number of cents to format.
     * @param precision - The number of decimal places to include.
     * @param thousands - The character to use as the thousands separator.
     * @param decimal - The character to use as the decimal separator.
     * @returns The formatted currency string.
     */
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) {
        return '0'
      }

      const numString = (number / 100.0).toFixed(precision)
      const parts = numString.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands)
      const cents = parts[1] ? decimal + parts[1] : ''
      return dollars + cents
    }

    return formatString.replace(placeholderRegex, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          // Ex.	1,134.65
          return formatWithDelimiters(cents, 2)
        case 'amount_no_decimals':
          // Ex. 1,135
          return formatWithDelimiters(cents, 0)
        case 'amount_with_comma_separator':
          // Ex. 1.134,65
          return formatWithDelimiters(cents, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          // Ex. 1.135
          return formatWithDelimiters(cents, 0, '.', ',')
        case 'amount_with_apostrophe_separator':
          // Ex. 1'134.65
          return formatWithDelimiters(cents, 2, "'", '.')
        case 'amount_no_decimals_with_space_separator':
          // Ex. 1 135
          return formatWithDelimiters(cents, 0, ' ')
        case 'amount_with_space_separator':
          // 1 134,65
          return formatWithDelimiters(cents, 2, ' ', ',')
        case 'amount_with_period_and_space_separator':
          // 1 134.65
          return formatWithDelimiters(cents, 2, ' ', '.')
        default:
          return match
      }
    })
  }

  // !SECTION: Discount Functions
  price(selling_plan, formatted = true) {
    if (this.rule) {
      return formatted
        ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100))
        : this.selectedVariant.price - this.discount(selling_plan).amount - this.selectedVariant.price * (this.rule.discountAmount / 100)
    }

    return formatted ? this.money(this.selectedVariant.price - this.discount(selling_plan).amount) : this.selectedVariant.price - this.discount(selling_plan).amount
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]')

    if ($sellingPlan) {
      $sellingPlan.value = this.selectedSellingPlan ? this.selectedSellingPlan.id : ''
      $sellingPlan.dispatchEvent(new Event('change'))
    }
  }

  // SECTION: Additional Functionality
  updateURLParams() {
    const url = new URL(window.location.href)

    if (this.layoutMode === 'supply' && this.supplySelection) {
      // Bundle selections point at a different product, so a base-product URL
      // with its selling_plan id would be inconsistent — use ?supply= instead.
      url.searchParams.delete('selling_plan')
      url.searchParams.set('supply', this.selectedSupplyDays)
    } else if (this.selectedSellingPlan) {
      url.searchParams.delete('supply')
      url.searchParams.set('selling_plan', this.selectedSellingPlan.id)
    } else {
      url.searchParams.delete('supply')
      url.searchParams.delete('selling_plan')
    }

    window.history.replaceState({}, '', url.href)
  }

  // Runs a fetch request to add the effective variant (bundle variant when a
  // 60/90-day supply option is selected) to the cart with the passed quantity
  addToCart(quantity) {
    const effectiveVariantId = this.layoutMode === 'supply' && this.supplySelection ? this.supplySelection.variant.id : this.selectedVariant.id

    const items = [
      {
        id: effectiveVariantId,
        quantity: quantity,
        ...(this.selectedSellingPlan && {
          selling_plan: this.selectedSellingPlan?.id,
        }),
      },
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
      }),
    })
      .then(response => response.json())
      .then(response => {
        this.log('Added item to cart: ', response)
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        })

        this.dispatchEvent(event)
      })
      .catch(error => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = handle => {
    return fetch(`/products/${handle}.js`)
      .then(response => response.json())
      .then(product => {
        this.product = product
        this.selectedVariant = product.variants[0]

        return product
      })
  }
  // !SECTION: Additional Functionality
}

if (!customElements.get('skio-plan-picker')) {
  customElements.define('skio-plan-picker', SkioPlanPicker)
}

if (window) window.SkioPlanPicker = SkioPlanPicker